import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Card, Grid, Code, Divider } from '../../components/DocComponents'

const toc = [
  { id: 'what-is-rabbit', title: 'What is Rabbit?', level: 2 as const },
  { id: 'core-services', title: 'Core services', level: 2 as const },
  { id: 'brainer', title: 'Brainer — the heart', level: 3 as const },
  { id: 'key-concepts', title: 'Key concepts', level: 2 as const },
  { id: 'when-to-use', title: 'When to use Rabbit', level: 2 as const },
]

export function Introduction() {
  return (
    <Article toc={toc}>
      <H1>Introduction to Rabbit</H1>
      <Lead>
        Rabbit is a multi-tenant automation platform that lets you model your business domain, define
        automated workflows, expose dashboards, and connect services — without writing backend code.
      </Lead>

      <H2 id="what-is-rabbit">What is Rabbit?</H2>
      <P>
        Rabbit provides a set of integrated services that together cover everything from user management
        and file storage to complex workflow automation and real-time dashboards. Whether you are building
        an internal operations tool, a customer-facing portal, or an event-driven integration layer,
        Rabbit gives you the building blocks.
      </P>
      <P>
        Every piece of data, every workflow, and every dashboard lives within a <strong>Business</strong> — the
        top-level tenant in Rabbit. One Rabbit installation can host multiple independent businesses with
        complete data isolation.
      </P>

      <H2 id="core-services">Core services</H2>
      <Grid cols={2}>
        <Card title="Auth Service" icon="🔐">
          JWT-based authentication, role-based access control, multi-tenancy, email verification, and a
          complete audit trail. Every API call is gated through here.
        </Card>
        <Card title="BUZZ (Events)" icon="⚡">
          A publish/subscribe event bus available over both REST and gRPC. Services emit events here;
          webhooks push them to your subscribers in real time.
        </Card>
        <Card title="Mail Service" icon="📬">
          Monitor IMAP mailboxes and automatically emit a BUZZ event whenever a new email arrives.
          Attachments are forwarded to Storage.
        </Card>
        <Card title="Storage Service" icon="🗄️">
          Multi-tenant file storage with dataset scoping, MIME-type awareness, role-based permissions,
          and automatic archival/purge policies.
        </Card>
        <Card title="Brainer" icon="🧠">
          The automation engine — data modeling, workflow execution, dashboards, and more. The largest
          and most powerful service in Rabbit.
        </Card>
        <Card title="Crawl (this site)" icon="📖">
          The official documentation reference for the entire Rabbit platform. No authentication required.
        </Card>
      </Grid>

      <H3 id="brainer">Brainer — the heart of Rabbit</H3>
      <P>
        Brainer deserves special attention because most of your day-to-day work happens there.
        It is split into three conceptual layers:
      </P>
      <UL>
        <LI><strong>Data model</strong> — Ingredients, Traits, Cups, and Relations define the shape of your data (think: tables and columns).</LI>
        <LI><strong>Workflows</strong> — Steps, Drinkers, Preparations, and Journeys define how data is collected, transformed, and processed.</LI>
        <LI><strong>Plates</strong> — Dashboard pages composed of Sections and Views that display live data and trigger workflows.</LI>
      </UL>

      <H2 id="key-concepts">Key concepts</H2>
      <P>You will encounter these terms throughout the documentation:</P>
      <UL>
        <LI><strong>Business</strong> — A tenant. All data belongs to exactly one business.</LI>
        <LI><strong>Cup</strong> — An entity type (like a database table). A <em>Coffee</em> is one record in that cup.</LI>
        <LI><strong>Trait</strong> — A reusable field definition (name, type, validation).</LI>
        <LI><strong>Preparation</strong> — A workflow made of ordered Steps. An instance of a running preparation is a <em>Journey</em>.</LI>
        <LI><strong>Drinker</strong> — A JavaScript script that queries and transforms data. Used to feed Views on Plates.</LI>
        <LI><strong>Plate</strong> — A dashboard page. Contains Sections, each holding Views.</LI>
        <LI><strong>OpenPlace</strong> — A publicly accessible Plate — no login required.</LI>
      </UL>

      <Callout type="tip" title="New to Rabbit?">
        Head to the <a href="/docs/quick-start" className="text-blue-600 underline underline-offset-2">Quick Start guide</a> for
        a hands-on walkthrough that takes you from zero to a working dashboard in under 10 minutes.
      </Callout>

      <H2 id="when-to-use">When to use Rabbit</H2>
      <P>Rabbit excels at:</P>
      <UL>
        <LI>Internal operational tools where users collect, validate, and process structured data</LI>
        <LI>Multi-step approval or data-entry workflows guided by an AI interviewer</LI>
        <LI>Real-time dashboards that display live records with filtering, sorting, and pagination</LI>
        <LI>Event-driven integrations where services communicate via the BUZZ bus</LI>
        <LI>Multi-tenant SaaS backends where each customer has isolated data</LI>
      </UL>

      <Callout type="note">
        Rabbit is not a replacement for a general-purpose web framework. It is purpose-built for
        structured data workflows. For free-form content sites or pure APIs, a traditional stack may be simpler.
      </Callout>

      <Divider />
      <P>
        Ready to dive in? The next section walks through the overall system <a href="/docs/architecture" className="text-blue-600 underline underline-offset-2">architecture</a>,
        followed by a hands-on <a href="/docs/quick-start" className="text-blue-600 underline underline-offset-2">quick start</a>.
      </P>
    </Article>
  )
}
